<template>
  <nav class="admin-menu-bar">
      <el-menu class="admin-menu" :router="true" :default-active="activeMenu" :unique-opened="true"
        background-color="transparent" text-color="var(--ui-text-primary)" active-text-color="var(--ui-color-primary)"
        :collapse-transition="false" :collapse="isCollapse" @open="handleMenuOpen" @close="handleMenuClose">
        <el-submenu
          v-for="route in routes"
          :key="route.path"
          :ref="getSubmenuRef(route.path)"
          class="terminal-icon-control-host"
          :class="{ 'terminal-control-click-flash': isMenuItemFlashing(route.path) }"
          :index="route.path"
          :hide-timeout="isSubmenuPinned(route.path) ? pinnedHideTimeout : 300"
          :popper-class="getSubmenuPopperClass(route)"
          @click.native="handleSubmenuClick($event, route.path)"
        >
          <template slot="title">
            <i class="ui-icon-glyph terminal-nav-icon admin-menu-icon" :class="[route.meta.icon, { 'terminal-control-click-flash': isMenuItemFlashing(route.path) }]"></i>
            <span :class="{ 'terminal-control-click-flash': isMenuItemFlashing(route.path) }">{{ $t(route.meta.title) }}</span>
          </template>

          <template v-for="moduleRoute in route.children">
            <el-menu-item
              v-if="moduleRoute.children.length < 2"
              :key="moduleRoute.path + '-single'"
              :index="moduleRoute.children[0].path"
              @click="handleMenuSelect(moduleRoute.children[0].path)"
            >
              <i class="ui-icon-glyph terminal-nav-icon admin-menu-icon" :class="[moduleRoute.children[0].meta.icon, { 'terminal-control-click-flash': isMenuItemFlashing(moduleRoute.children[0].path) }]"></i>
              <span slot="title" :class="{ 'terminal-control-click-flash': isMenuItemFlashing(moduleRoute.children[0].path) }">{{ $t(moduleRoute.children[0].meta.title) }}</span>
            </el-menu-item>

            <el-submenu
              v-else
              :key="moduleRoute.path + '-group'"
              :ref="getSubmenuRef(moduleRoute.path)"
              :index="moduleRoute.path"
              :show-timeout="getNestedSubmenuShowTimeout(moduleRoute.path)"
              :hide-timeout="isSubmenuPinned(moduleRoute.path) ? pinnedHideTimeout : 300"
              :popper-append-to-body="true"
              :popper-options="nestedPopperOptions"
              popper-class="terminal-dropdown-menu admin-menu-submenu-popper admin-menu-submenu-nested"
              @click.native="handleSubmenuClick($event, moduleRoute.path)"
            >
              <template slot="title">
                <i class="ui-icon-glyph terminal-nav-icon admin-menu-icon" :class="[moduleRoute.meta.icon, { 'terminal-control-click-flash': isMenuItemFlashing(moduleRoute.path) }]"></i>
                <span :class="{ 'terminal-control-click-flash': isMenuItemFlashing(moduleRoute.path) }">{{ $t(moduleRoute.meta.title) }}</span>
              </template>
              <el-menu-item
                v-for="pageRoute in moduleRoute.children"
                :key="pageRoute.path"
                :index="pageRoute.path"
                @click="handleMenuSelect(pageRoute.path)"
              >
                <i class="ui-icon-glyph terminal-nav-icon admin-menu-icon" :class="[pageRoute.meta.icon, { 'terminal-control-click-flash': isMenuItemFlashing(pageRoute.path) }]"></i>
                <span :class="{ 'terminal-control-click-flash': isMenuItemFlashing(pageRoute.path) }">{{ $t(pageRoute.meta.title) }}</span>
              </el-menu-item>
            </el-submenu>
          </template>
        </el-submenu>
      </el-menu>
  </nav>
</template>

<script>
import { canAccessRouteMeta } from '@/utils/authorization'
import { getRouteWorkspace, WORKSPACE_ADMIN } from '@/utils/workspace'
import { buildAdminNavigationGroups } from '@/router/adminNavigation'

export default {
  name: 'AdminMenuBar',
  props: {
    collapsed: { type: Boolean, default: null }
  },
  data () {
    return {
      pinnedSubmenuIndexes: [],
      pinnedHideTimeout: 2147483647,
      flashingMenuIndex: '',
      menuFlashTimer: null,
      submenuAnimationFrame: null,
      // 子窗口从父窗口右侧展开，与左侧导航的一级菜单同向堆叠。
      nestedPopperOptions: {
        gpuAcceleration: false,
        modifiers: ['shift', 'offset', 'keepTogether', 'arrow', 'applyStyle']
      }
    }
  },
  computed: {
    /**
     * @description: 先按页面权限和模块配置过滤，再生成后台分级导航树
    */
    routes () {
      const canAccess = (meta = {}) => canAccessRouteMeta(this.$store.state.user, meta)
      const filterRoutes = (routes) => {
        return routes.reduce((result, route) => {
          const item = { ...route }
          if (item.meta.hidden === true || !canAccess(item.meta) || getRouteWorkspace(item) !== WORKSPACE_ADMIN) {
            return result
          }
          if (item.children && item.children.length > 0) {
            item.children = filterRoutes(item.children)
            if (item.children.length === 0) return result
          }
          result.push(item)
          return result
        }, [])
      }
      const terminalRoute = this.$router.options.routes.find(route => route.meta && route.meta.terminalShell)
      const moduleRoutes = filterRoutes(terminalRoute ? terminalRoute.children : [])
        .filter(route => this.isModuleVisible(this.getModuleKey(route)))
      return buildAdminNavigationGroups(moduleRoutes)
    },
    /**
     * @description: 当前激活的 路由路径
     */
    activeMenu () {
      const route = this.$route
      const { meta, path } = route
      const decodedPath = decodeURIComponent(path) // 解码 URL
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return decodedPath
    },

    /**
     * @description: 侧边导航栏状态
     */
    isCollapse () {
      return this.collapsed === null ? this.$store.getters['app/getIsCollapse'] : this.collapsed
    }
  },
  mounted () {
    this.inlineSubmenuAnimationStates = Object.create(null)
    document.addEventListener('pointerdown', this.handleDocumentPointerDown, true)
    this.$nextTick(this.syncNestedSubmenuPlacements)
  },
  updated () {
    this.syncNestedSubmenuPlacements()
  },
  beforeDestroy () {
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true)
    clearTimeout(this.menuFlashTimer)
    if (this.submenuAnimationFrame !== null) cancelAnimationFrame(this.submenuAnimationFrame)
    Object.keys(this.inlineSubmenuAnimationStates || {}).forEach(this.cancelInlineSubmenuAnimation)
    this.closeAllPinnedSubmenus()
  },
  methods: {
    /**
     * @description: 屏幕宽度小于480 隐藏侧边栏
     */
    lt480Collapse () {
      if (this.isCollapse) return
      // 判断屏幕宽度是否小于 480 像素
      if (this.$store.state.app.isWideScreen) {
        setTimeout(() => {
          if (!this.isCollapse) this.$store.commit('app/setCollapseState', true)
        }, 100)
      }
    },
    isModuleVisible (key) {
      return this.$store.getters['app/getModuleVisible'](key)
    },
    getModuleKey (route) {
      return String(route.path || '').replace(/^\//, '').split('/')[0]
    },
    getSubmenuRef (index) {
      return `submenu:${index}`
    },
    getSubmenuInstance (index) {
      const ref = this.$refs[this.getSubmenuRef(index)]
      return Array.isArray(ref) ? ref[0] : ref
    },
    syncNestedSubmenuPlacements () {
      this.routes.forEach(route => {
        route.children
          .filter(moduleRoute => moduleRoute.children.length > 1)
          .forEach(moduleRoute => this.setNestedSubmenuPlacement(moduleRoute.path))
      })
    },
    setNestedSubmenuPlacement (index) {
      const submenu = this.getSubmenuInstance(index)
      if (!submenu || submenu.currentPlacement === 'right-start') return
      const wasOpened = submenu.opened
      submenu.currentPlacement = 'right-start'
      if (!submenu.popperJS) return
      submenu.doDestroy(true)
      if (wasOpened) this.$nextTick(() => submenu.updatePopper())
    },
    isSubmenuPinned (index) {
      return this.pinnedSubmenuIndexes.includes(index)
    },
    getNestedSubmenuShowTimeout (index) {
      const pinnedNestedIndex = this.pinnedSubmenuIndexes.find(item => String(item).startsWith('/'))
      return pinnedNestedIndex && pinnedNestedIndex !== index ? this.pinnedHideTimeout : 300
    },
    isMenuItemFlashing (index) {
      return this.flashingMenuIndex === index
    },
    flashMenuItem (index) {
      clearTimeout(this.menuFlashTimer)
      this.flashingMenuIndex = ''
      this.$nextTick(() => {
        this.flashingMenuIndex = index
        this.menuFlashTimer = setTimeout(() => {
          this.flashingMenuIndex = ''
          this.menuFlashTimer = null
        }, 660)
      })
    },
    hasNestedMenu (route) {
      return route.children.some(moduleRoute => moduleRoute.children.length > 1)
    },
    getSubmenuPopperClass (route) {
      return [
        'terminal-dropdown-menu',
        'admin-menu-submenu-popper',
        'admin-menu-submenu-root',
        this.hasNestedMenu(route) ? 'has-nested-menu' : ''
      ].filter(Boolean).join(' ')
    },
    isOwnSubmenuTitle (submenu, target) {
      const title = submenu && submenu.$refs ? submenu.$refs['submenu-title'] : null
      return Boolean(title && target && title.contains(target))
    },
    handleSubmenuClick (event, index) {
      const submenu = this.getSubmenuInstance(index)
      if (!this.isOwnSubmenuTitle(submenu, event.target)) return
      this.flashMenuItem(index)
      if (!this.isCollapse) return
      event.preventDefault()
      event.stopPropagation()
      if (this.isSubmenuPinned(index) && submenu.opened) {
        this.closePinnedSubmenu(index)
      } else {
        this.openPinnedSubmenu(index)
      }
    },
    handleMenuOpen (index) {
      if (!this.isCollapse) {
        this.animateInlineSubmenu(index, true)
        return
      }
      this.$nextTick(() => {
        if (this.submenuAnimationFrame !== null) cancelAnimationFrame(this.submenuAnimationFrame)
        this.submenuAnimationFrame = requestAnimationFrame(() => {
          this.submenuAnimationFrame = null
          const submenu = this.getSubmenuInstance(index)
          const menu = submenu && submenu.$refs ? submenu.$refs.menu : null
          if (!menu) return
          const surface = menu.matches?.('.el-menu--popup')
            ? menu
            : menu.querySelector('.el-menu--popup')
          if (!surface) return
          const animationClass = 'admin-submenu-fading'
          surface.classList.remove(animationClass)
          surface.getBoundingClientRect()
          surface.classList.add(animationClass)
          surface.addEventListener('animationend', () => {
            surface.classList.remove(animationClass)
          }, { once: true })
        })
      })
    },
    handleMenuClose (index) {
      if (!this.isCollapse) this.animateInlineSubmenu(index, false)
    },
    getInlineSubmenuSurface (index) {
      const submenu = this.getSubmenuInstance(index)
      const children = submenu && submenu.$el ? Array.from(submenu.$el.children) : []
      return children.find(child => child.classList.contains('el-menu--inline')) || null
    },
    cancelInlineSubmenuAnimation (index) {
      const state = this.inlineSubmenuAnimationStates && this.inlineSubmenuAnimationStates[index]
      if (!state) return
      cancelAnimationFrame(state.frame)
      clearTimeout(state.timer)
      state.surface.removeEventListener('transitionend', state.finish)
      delete this.inlineSubmenuAnimationStates[index]
    },
    animateInlineSubmenu (index, opening) {
      const surfaceBeforeUpdate = this.getInlineSubmenuSurface(index)
      const visibleHeight = surfaceBeforeUpdate
        ? Math.max(surfaceBeforeUpdate.getBoundingClientRect().height, surfaceBeforeUpdate.scrollHeight)
        : 0

      this.$nextTick(() => {
        const surface = this.getInlineSubmenuSurface(index)
        if (!surface) return
        this.cancelInlineSubmenuAnimation(index)

        // v-show 会在关闭时立即写入 display:none，动画前先恢复参与布局。
        surface.style.display = 'block'
        surface.style.transition = 'none'
        surface.style.overflow = 'hidden'

        const fullHeight = Math.max(visibleHeight, surface.scrollHeight)
        const startHeight = opening ? 0 : fullHeight
        const endHeight = opening ? fullHeight : 0
        surface.style.height = `${startHeight}px`
        surface.getBoundingClientRect()

        const finish = (event) => {
          if (event && (event.target !== surface || event.propertyName !== 'height')) return
          const state = this.inlineSubmenuAnimationStates[index]
          if (!state || state.surface !== surface) return
          this.cancelInlineSubmenuAnimation(index)
          surface.style.removeProperty('transition')
          surface.style.removeProperty('height')
          surface.style.removeProperty('overflow')
          if (opening) surface.style.removeProperty('display')
          else surface.style.display = 'none'
        }

        surface.addEventListener('transitionend', finish)
        const frame = requestAnimationFrame(() => {
          surface.style.transition = 'height var(--ui-duration-menu-expand) cubic-bezier(.22, .72, .2, 1)'
          surface.style.height = `${endHeight}px`
        })
        const timer = setTimeout(() => finish(), 700)
        this.$set(this.inlineSubmenuAnimationStates, index, { surface, frame, timer, finish })
      })
    },
    openPinnedSubmenu (index) {
      const submenu = this.getSubmenuInstance(index)
      if (!submenu || !submenu.rootMenu) return
      const indexPath = Array.isArray(submenu.indexPath) ? submenu.indexPath : [index]
      this.pinnedSubmenuIndexes.slice().forEach(pinnedIndex => {
        if (!indexPath.includes(pinnedIndex)) this.closePinnedSubmenu(pinnedIndex)
      })
      // Element UI 折叠菜单会为同级项保留悬停延时；点击固定当前项时必须一并取消，
      // 否则旧定时器稍后执行会把当前子菜单错误切换到另一个同级菜单。
      const siblingSubmenus = submenu.parentMenu && submenu.parentMenu.submenus
        ? Object.values(submenu.parentMenu.submenus)
        : []
      siblingSubmenus.forEach(sibling => {
        clearTimeout(sibling.timeout)
        if (sibling === submenu) return
        sibling.mouseInChild = false
        submenu.rootMenu.closeMenu(sibling.index)
      })
      if (!this.isSubmenuPinned(index)) this.pinnedSubmenuIndexes.push(index)
      this.$nextTick(() => {
        clearTimeout(submenu.timeout)
        submenu.mouseInChild = true
        submenu.rootMenu.openMenu(submenu.index, submenu.indexPath)
      })
    },
    closePinnedSubmenu (index) {
      const submenu = this.getSubmenuInstance(index)
      this.pinnedSubmenuIndexes = this.pinnedSubmenuIndexes.filter(item => item !== index)
      if (!submenu) return
      clearTimeout(submenu.timeout)
      submenu.mouseInChild = false
      if (submenu.rootMenu) submenu.rootMenu.closeMenu(submenu.index)
    },
    closeAllPinnedSubmenus () {
      this.pinnedSubmenuIndexes.slice().reverse().forEach(this.closePinnedSubmenu)
    },
    handleDocumentPointerDown (event) {
      if (this.pinnedSubmenuIndexes.length === 0) return
      const clickedInsidePinnedMenu = this.pinnedSubmenuIndexes.some(index => {
        const submenu = this.getSubmenuInstance(index)
        const reference = submenu && submenu.$el
        const popup = submenu && submenu.$refs ? submenu.$refs.menu : null
        return (reference && reference.contains(event.target)) || (popup && popup.contains(event.target))
      })
      if (!clickedInsidePinnedMenu) this.closeAllPinnedSubmenus()
    },
    handleMenuSelect (index) {
      this.flashMenuItem(index)
      this.closeAllPinnedSubmenus()
      this.lt480Collapse()
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-menu-bar {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.admin-menu {
  min-height: 100%;
  border: 0;
  font-size: var(--ui-font-size-navigation);
}

.admin-menu-icon {
  margin: 0 var(--ui-space-sm) 0 0 !important;
  color: var(--ui-text-primary);
}

.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-menu-item > .admin-menu-icon,
.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-submenu__title > .admin-menu-icon {
  color: var(--ui-color-primary) !important;
}

.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-menu-item,
.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-submenu__title {
  display: flex;
  align-items: center;
  font-size: var(--ui-font-size-navigation);
  transition: color var(--ui-duration-fast) ease,
    background-color var(--ui-duration-fast) ease;
}

.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) > .el-menu-item,
.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) > .el-submenu {
  animation: admin-menu-slide-open var(--ui-duration-normal) var(--ui-ease-standard) both;
}

.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-menu-item:focus-visible,
.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-submenu__title:focus-visible {
  color: var(--ui-text-primary) !important;
  background: var(--ui-bg-panel-elevated) !important;
}

@media (hover: hover) and (pointer: fine) {
  .admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-menu-item:hover,
  .admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-submenu__title:hover {
    color: var(--ui-text-primary) !important;
    background: var(--ui-bg-panel-elevated) !important;
  }
}

.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-menu-item.is-active {
  color: var(--ui-text-primary) !important;
  background: rgba(var(--ui-color-primary-rgb), .1) !important;
  box-shadow: inset -2px 0 var(--ui-color-primary);
  font-weight: var(--ui-font-weight-medium);
}

/* 父级只表达“当前页面归属”，视觉权重必须低于真正选中的叶子页面。 */
.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-submenu.is-active > .el-submenu__title {
  color: var(--ui-text-primary) !important;
  background: rgba(var(--ui-color-primary-rgb), .035) !important;
  box-shadow: inset -1px 0 rgba(var(--ui-color-primary-rgb), .22);
  font-weight: var(--ui-font-weight-medium);
}

/* 仅展开、但不包含当前页面的父级保持普通导航语义。 */
.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-submenu.is-opened:not(.is-active) > .el-submenu__title {
  color: var(--ui-text-primary) !important;
  background: var(--ui-bg-panel-elevated) !important;
  box-shadow: none;
}

.admin-menu-bar ::v-deep .admin-submenu-fading {
  animation: admin-submenu-fade-in var(--ui-duration-normal) ease both;
}

.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-menu-item > span,
.admin-menu-bar ::v-deep .admin-menu:not(.el-menu--collapse) .el-submenu__title > span {
  line-height: 1.25;
}

.el-submenu {
  .el-menu-item {
    background-color: rgba(var(--ui-color-black-rgb), .14) !important;
  }
}

@media (hover: hover) and (pointer: fine) {
  .el-submenu .el-menu-item:hover {
    background-color: rgba(var(--ui-color-primary-rgb), .06) !important;
  }
}

/* 占位与实际按钮同宽，顶部留白跟随水平居中的两侧留白。 */
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse {
  --terminal-collapsed-control-size: var(--ui-icon-control-size);
  width: 100%;
  padding: max(0px, calc((100% - var(--terminal-collapsed-control-size)) / 2)) 0;
  box-sizing: border-box;
}

.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-menu-item,
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-submenu {
  width: var(--terminal-collapsed-control-size, 40px);
  height: var(--terminal-collapsed-control-size, 40px);
  margin: 0 auto var(--terminal-collapsed-control-gap, 10px);
  animation: admin-collapsed-tool-deal-in .3s cubic-bezier(.2, .76, .24, 1) both;
}

.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(1) { animation-delay: 35ms; }
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(2) { animation-delay: 80ms; }
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(3) { animation-delay: 125ms; }
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(4) { animation-delay: 170ms; }
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(5) { animation-delay: 215ms; }
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(6) { animation-delay: 260ms; }
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(7) { animation-delay: 305ms; }
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > :nth-child(8) { animation-delay: 350ms; }

@keyframes admin-collapsed-tool-deal-in {
  from {
    opacity: 0;
    transform: translate3d(9px, -14px, 0) rotate(-3deg) scale(.9);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0) rotate(0) scale(1);
  }
}

.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-menu-item,
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-submenu > .el-submenu__title {
  position: relative;
  width: var(--terminal-collapsed-control-size, 40px);
  height: var(--terminal-collapsed-control-size, 40px);
  line-height: var(--terminal-collapsed-control-size, 40px);
}

.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-menu-item > .admin-menu-icon,
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-menu-item > .el-tooltip > .admin-menu-icon,
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-submenu > .el-submenu__title > .admin-menu-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  margin: 0 !important;
  color: inherit;
  transform: translate(-50%, -50%);
}

/* Element UI 会为无子项菜单额外包一层 tooltip，清除它自带的左右内边距。 */
.admin-menu-bar ::v-deep .admin-menu.el-menu--collapse > .el-menu-item > .el-tooltip {
  padding: 0 !important;
}

@keyframes admin-menu-slide-open {
  from {
    opacity: 0;
    transform: translateX(16px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes admin-submenu-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
